/** @module The sales list: selected copies folded and priced, to list them on Cardmarket by hand. */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { Sheet } from "@/components/ui/overlays";
import { variantLabel } from "@/lib/cards";
import { filenameFrom, saveBlob } from "@/lib/download";
import { count, money } from "@/lib/format";

async function fetchSalesList(ids: string[]) {
  const { data, response } = await api.POST("/api/v1/collection/sales-list", {
    body: { item_ids: ids },
  });
  if (!data) throw problemFrom(response);
  return data;
}

export function SalesSheet({
  ids,
  open,
  onClose,
}: {
  ids: string[];
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const list = useQuery({
    queryKey: ["sales-list", ids],
    queryFn: () => fetchSalesList(ids),
    enabled: open && ids.length > 0,
  });
  const download = useMutation({
    mutationFn: async () => {
      const { data, response } = await api.POST("/api/v1/collection/sales-list/csv", {
        body: { item_ids: ids },
        parseAs: "blob",
      });
      if (!data) throw problemFrom(response);
      saveBlob(data, filenameFrom(response, "poke-scan-verkaufsliste.csv"));
    },
  });
  const rows = list.data?.items ?? [];
  const copies = rows.reduce((sum, row) => sum + row.quantity, 0);

  return (
    <Sheet open={open} onClose={onClose} label={t("transfer.sales.title")}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="t-h3">{t("transfer.sales.title")}</h2>
          <span className="t-sm t-muted">{t("transfer.sales.hint")}</span>
        </div>
        {list.isPending ? <p className="t-muted">{t("common.loading")}</p> : null}
        {list.isError ? (
          <p role="alert" className="loss">
            {t("common.loadError")}
          </p>
        ) : null}
        {list.data && rows.length === 0 ? (
          <p className="t-sm t-muted">{t("transfer.sales.empty")}</p>
        ) : null}
        {rows.length > 0 ? (
          <>
            <ul className="flex flex-col">
              {rows.map((row) => (
                <li
                  key={`${String(row.variant_id)}-${row.language}-${row.condition}`}
                  className="row min-h-14 gap-3 border-b border-(--border) py-2 last:border-0"
                >
                  <span className="num w-8 shrink-0 text-right">{row.quantity}×</span>
                  <span className="flex min-w-0 grow flex-col">
                    <b className="t-sm truncate">{row.name ?? row.print_id}</b>
                    <span className="t-xs t-muted num truncate">
                      {row.set_code ?? ""} {row.number}
                      {row.set_total ? `/${String(row.set_total)}` : ""}
                      {row.variant
                        ? ` · ${variantLabel(row.variant, (key, fallback) => t(key, fallback))}`
                        : ""}
                      {` · ${row.language.toUpperCase()} · ${row.condition}`}
                    </span>
                  </span>
                  <span className="num shrink-0 text-right">
                    {money(row.suggested_price, locale) ?? (
                      <span className="t-xs t-muted">{t("collection.unvalued")}</span>
                    )}
                  </span>
                  {row.cardmarket_url ? (
                    <a
                      href={row.cardmarket_url}
                      target="_blank"
                      rel="noreferrer"
                      className="icon-btn shrink-0"
                      aria-label={t("transfer.sales.open", { name: row.name ?? row.print_id })}
                      title={t("transfer.sales.open", { name: row.name ?? row.print_id })}
                    >
                      <ExternalLink />
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
            <div className="row-between flex-wrap gap-2">
              <span className="t-sm">
                <b className="num">
                  {t("transfer.sales.total", {
                    copies: count(copies, locale),
                    value: money(list.data?.total_suggested, locale),
                  })}
                </b>
                {list.data && list.data.unpriced > 0 ? (
                  <span className="t-muted">
                    {" · "}
                    {t("transfer.sales.unpriced", { count: list.data.unpriced })}
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={download.isPending}
                onClick={() => {
                  download.mutate();
                }}
              >
                <Download />
                {t("transfer.sales.csv")}
              </button>
            </div>
            {download.isError ? (
              <p role="alert" className="t-sm loss">
                {t("transfer.exportError")}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </Sheet>
  );
}
